import EmptyState from "../../../../../shared/components/EmptyState";
import Spinner from "../../../../../shared/components/Spinner";
import { siteTexts } from "../../../../../shared/i18n/siteTexts";
import { formatEventDateRangeLabel } from "../../../../../shared/utils/dateTime";
import type { MyGameItem } from "../../../shared/services/registrationPaymentsService";
import ProfileAccordionSection from "../ProfileAccordionSection";
import UserProfileSectionHeader from "../UserProfileSectionHeader";

type ProfileMyGamesTabProps = {
  open: boolean;
  onToggle: () => void;
  upcomingGames: MyGameItem[];
  historyGames: MyGameItem[];
  loading: boolean;
  error: string;
};

type ProfileMyGamesContentProps = {
  upcomingGames: MyGameItem[];
  historyGames: MyGameItem[];
  loading: boolean;
  error: string;
};

function myGameResultLabel(game: MyGameItem) {
  const labels = siteTexts.profile.myGames.result;
  if (game.userResult === "win") return labels.win;
  if (game.userResult === "loss") return labels.loss;
  if (String(game.matchStatus || "").toLowerCase() === "finished") {
    return labels.finished;
  }
  return labels.pending;
}

function myGameScheduleLabel(game: MyGameItem) {
  if (!game.slotStartAt) return siteTexts.profile.myGames.unscheduled;
  return formatEventDateRangeLabel(game.slotStartAt, game.slotEndAt);
}

export function ProfileMyGamesContent({
  upcomingGames,
  historyGames,
  loading,
  error,
}: ProfileMyGamesContentProps) {
  const hasGames = upcomingGames.length > 0 || historyGames.length > 0;

  return (
    <>
      {loading && (
        <p className="status-message">
          <Spinner label={siteTexts.common.loading} />
        </p>
      )}
      {error && <p className="status-message">{error}</p>}
      {!loading && !error && !hasGames && (
        <EmptyState
          className="profile-empty-state"
          icon={
            <span className="material-symbols-outlined">sports_tennis</span>
          }
          title={siteTexts.profile.myGames.empty}
        />
      )}
      {!loading && !error && hasGames && (
        <div className="profile-my-games-shell">
          <section className="profile-my-games-group">
            <div className="profile-my-games-group-header">
              <h3>{siteTexts.profile.myGames.upcomingTitle}</h3>
              <span>{upcomingGames.length}</span>
            </div>
            {upcomingGames.length === 0 ? (
              <p className="profile-my-games-empty">
                {siteTexts.profile.myGames.noUpcoming}
              </p>
            ) : (
              <div className="profile-my-games-list">
                {upcomingGames.map((game) => (
                  <article
                    key={`my-upcoming-game-${game.matchId}`}
                    className="profile-my-game-card"
                  >
                    <div className="profile-my-game-main">
                      <span className="profile-my-game-kicker">
                        {game.categoryName ||
                          game.categoryKey ||
                          siteTexts.profile.myGames.categoryFallback}
                      </span>
                      <h3>{game.tournamentName}</h3>
                      <strong>{game.matchLabel}</strong>
                      <p>
                        {game.sideALabel} x {game.sideBLabel}
                      </p>
                    </div>
                    <div className="profile-my-game-schedule">
                      <span
                        className="material-symbols-outlined"
                        aria-hidden="true"
                      >
                        event
                      </span>
                      <div>
                        <strong>{myGameScheduleLabel(game)}</strong>
                        <small>
                          {game.courtName ||
                            siteTexts.profile.myGames.courtFallback}
                        </small>
                        {game.tournamentAddress && (
                          <small>{game.tournamentAddress}</small>
                        )}
                      </div>
                    </div>
                    <a
                      className="profile-my-game-detail-link"
                      href={game.detailUrl}
                    >
                      {siteTexts.profile.myGames.viewTournament}
                    </a>
                  </article>
                ))}
              </div>
            )}
          </section>

          <section className="profile-my-games-group">
            <div className="profile-my-games-group-header">
              <h3>{siteTexts.profile.myGames.historyTitle}</h3>
              <span>{historyGames.length}</span>
            </div>
            {historyGames.length === 0 ? (
              <p className="profile-my-games-empty">
                {siteTexts.profile.myGames.noHistory}
              </p>
            ) : (
              <div className="profile-my-games-list">
                {historyGames.map((game) => (
                  <article
                    key={`my-history-game-${game.matchId}`}
                    className={`profile-my-game-card profile-my-game-card--${game.userResult || "finished"}`}
                  >
                    <div className="profile-my-game-main">
                      <span className="profile-my-game-kicker">
                        {game.categoryName ||
                          game.categoryKey ||
                          siteTexts.profile.myGames.categoryFallback}
                      </span>
                      <h3>{game.tournamentName}</h3>
                      <strong>{game.matchLabel}</strong>
                      <p>
                        {game.sideALabel} x {game.sideBLabel}
                      </p>
                    </div>
                    <div className="profile-my-game-result">
                      <span
                        className={`profile-my-game-result-pill profile-my-game-result-pill--${game.userResult || "finished"}`}
                      >
                        {myGameResultLabel(game)}
                      </span>
                      <strong>
                        {game.scoreLabel ||
                          siteTexts.profile.myGames.scorePending}
                      </strong>
                      <small>{myGameScheduleLabel(game)}</small>
                    </div>
                    <a
                      className="profile-my-game-detail-link"
                      href={game.detailUrl}
                    >
                      {siteTexts.profile.myGames.viewTournament}
                    </a>
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </>
  );
}

export default function ProfileMyGamesTab({
  open,
  onToggle,
  upcomingGames,
  historyGames,
  loading,
  error,
}: ProfileMyGamesTabProps) {
  return (
    <ProfileAccordionSection
      id="profile-my-games"
      icon="sports_tennis"
      title={siteTexts.profile.myGames.title}
      subtitle={siteTexts.profile.myGames.subtitle}
      open={open}
      onToggle={onToggle}
    >
      <div className="user-profile-content-section profile-my-games-content">
        <UserProfileSectionHeader
          className="profile-my-games-header"
          icon="sports_tennis"
          title={siteTexts.profile.myGames.title}
          description={siteTexts.profile.myGames.subtitle}
        />
        <ProfileMyGamesContent
          upcomingGames={upcomingGames}
          historyGames={historyGames}
          loading={loading}
          error={error}
        />
      </div>
    </ProfileAccordionSection>
  );
}
